import {
  ArrowsRightLeftIcon,
  AtSymbolIcon as AtSymbolIcon16,
  ChatBubbleLeftIcon as ChatBubbleLeftIcon16,
  CheckIcon as CheckIcon16,
  PlusIcon,
} from "@heroicons/react/16/solid";
import type { NotificationOutput } from "@orsane/schema/notifications";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { differenceInDays, formatDistanceStrict } from "date-fns";
import { MailOpen, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { CSSTransition } from "react-transition-group";
import { toast } from "sonner";
import { useDateFormatter } from "@/hooks/useUserPreferences";
import type { DateFormatter } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  useDeleteNotification,
  useMarkNotificationAsRead,
  useUndeleteNotification,
} from "@/mutations/notification-mutations";
import { inboxRoute } from "@/routes/inboxRoute";
import { UserAvatar } from "../UserAvatar";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "../ui/context-menu";
import { getNotificationSearchParams } from "./notification-search-params";

const formatCreatedDate = (date: Date, formatShortDate: DateFormatter["formatShortDate"]) => {
  // if difference is less than a day, use formatDistanceStrict
  if (differenceInDays(date, new Date()) < 1) {
    return formatDistanceStrict(date, new Date(), {
      addSuffix: false,
    })
      .replace("minutes", "min")
      .replace("hour", "hr");
  } else {
    return formatShortDate(date);
  }
};

const formatMessage = (notification: NotificationOutput) => {
  if (notification.taskList) {
    return `${notification.otherUser.name} added you to the list`;
  }
  if (notification.type === "task_assigned") {
    return "assigned you to a task";
  }
  if (notification.type === "task_comment_mentioned") {
    return "mentioned you in a comment";
  }
  if (notification.type === "task_comment_replied") {
    return "replied to your comment";
  }
  if (notification.type === "task_commented") {
    return "commented on a task";
  }
  if (notification.type === "task_description_mentioned") {
    return "mentioned you in the description";
  }
  if (notification.type === "task_completed") {
    return "completed a task you created";
  }

  return "";
};

const getIcon = (notification: NotificationOutput) => {
  if (notification.type === "task_commented") {
    return <ChatBubbleLeftIcon16 className="block text-zinc-800" />;
  }
  if (notification.type === "task_assigned") {
    return <ArrowsRightLeftIcon className="block text-zinc-800" />;
  }
  if (notification.type === "task_description_mentioned") {
    return <AtSymbolIcon16 className="block text-zinc-800" />;
  }
  if (notification.type === "task_comment_mentioned") {
    return <AtSymbolIcon16 className="block text-zinc-800" />;
  }
  if (notification.type === "task_comment_replied") {
    return <ChatBubbleLeftIcon16 className="block text-zinc-800" />;
  }
  if (notification.type === "task_list_added_user") {
    return <PlusIcon className="block text-zinc-800" />;
  }
  if (notification.type === "task_completed") {
    return <CheckIcon16 className="block text-zinc-800" />;
  }
  return null;
};

export default function InboxItem({
  notification,
  showAnimation,
  nextNotification,
}: {
  notification: NotificationOutput;
  showAnimation: boolean;
  nextNotification?: NotificationOutput;
}) {
  const { mutate: markAsRead } = useMarkNotificationAsRead();
  const { formatShortDate } = useDateFormatter();
  const { mutate: deleteNotification } = useDeleteNotification();
  const { mutate: undeleteNotification } = useUndeleteNotification();
  const navigate = useNavigate();
  const { notification: notificationId } = useSearch({
    from: inboxRoute.id,
    select: (s) => ({ notification: s.notification }),
  });
  const isSelected = notificationId === notification.id;
  const alreadyCheckedReadRef = useRef(false);
  const [showItem, setShowItem] = useState(false);
  const nodeRef = useRef(null);

  useEffect(() => {
    // Trigger fade-in animation on mount. but only after muonted
    setShowItem(true);
  }, []);

  useEffect(() => {
    // mark the selected task as read after 1.5 seconds
    let timer: NodeJS.Timeout | null = null;
    if (isSelected) {
      if (notification.readAt === null && !alreadyCheckedReadRef.current) {
        timer = setTimeout(() => {
          markAsRead({ id: notification.id, isRead: true });
          alreadyCheckedReadRef.current = true;
        }, 1500);
      } else {
        alreadyCheckedReadRef.current = true;
      }
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isSelected, markAsRead, notification]);

  const handleMarkAsUnread = () => {
    markAsRead({ id: notification.id, isRead: false });
    alreadyCheckedReadRef.current = true;
  };
  const handleMarkAsRead = () => {
    markAsRead({ id: notification.id, isRead: true });
    alreadyCheckedReadRef.current = true;
  };

  const handleRemoveFromInbox = () => {
    deleteNotification({ id: notification.id });

    toast.success("Notification removed", {
      description: "The notification has been removed from your inbox.",
      action: {
        label: "Undo",
        onClick: () => {
          undeleteNotification({ id: notification.id });
        },
      },
    });

    if (isSelected) {
      if (nextNotification) {
        navigate({
          to: inboxRoute.to,
          search: (prev) => ({
            ...prev,
            ...getNotificationSearchParams(nextNotification),
          }),
        });
      } else {
        navigate({
          to: inboxRoute.to,
          search: (prev) => ({
            ...prev,
            notification: undefined,
            task: undefined,
            taskList: undefined,
            tab: undefined,
          }),
        });
      }
    }
  };

  useHotkeys("backspace, delete", () => handleRemoveFromInbox(), {
    enabled: isSelected,
  });

  const unread = notification.readAt === null;

  const searchParam = getNotificationSearchParams(notification);

  return (
    <CSSTransition
      enter={showAnimation}
      in={showItem}
      timeout={400}
      classNames="inbox-item-fade"
      nodeRef={nodeRef}
      unmountOnExit
    >
      <div ref={nodeRef}>
        <ContextMenu>
          <ContextMenuTrigger
            render={
              <div className="pl-1 pr-2.5">
                <Link
                  to={inboxRoute.to}
                  search={searchParam}
                  className={cn(
                    "hover:bg-slate-200 p-2 rounded-sm flex items-center gap-3 select-none overflow-hidden font-[450] text-zinc-900 outline-none focus:outline-none focus-visible:outline-none",
                    {
                      "bg-zinc-200/60": isSelected,
                    },
                  )}
                >
                  <div className="shrink-0 relative">
                    <UserAvatar user={notification.otherUser} size="md" />
                    <div className="bg-white rounded-full size-3.5 flex items-center justify-center absolute -bottom-1 -right-1 p-[2px]">
                      {getIcon(notification)}
                    </div>
                  </div>
                  {/* See https://github.com/radix-ui/primitives/issues/926#issuecomment-2763624511 */}
                  <div className="flex-1 w-0 grow">
                    <div
                      className={cn(
                        "flex items-center",
                        unread && "font-medium",
                      )}
                    >
                      {unread && (
                        <div className="size-2 bg-blue-500 rounded-full mr-1.5"></div>
                      )}
                      <div className="flex-1 truncate">
                        {notification.task &&
                          `T-${notification.task.number} ${notification.task.title}`}
                        {notification.taskList &&
                          `${notification.taskList.name}`}
                      </div>
                    </div>
                    <div className="sm text-zinc-500 truncate">
                      {formatMessage(notification)}
                    </div>
                  </div>
                  <div className="ml-auto shrink-0">
                    <div className="text-sm text-zinc-400">
                      {formatCreatedDate(notification.createdAt, formatShortDate)}
                    </div>
                  </div>
                </Link>
              </div>
            }
          ></ContextMenuTrigger>
          <ContextMenuContent className="w-52">
            {!unread && (
              <ContextMenuItem onClick={handleMarkAsUnread}>
                <MailOpen />
                Mark as unread
              </ContextMenuItem>
            )}
            {unread && (
              <ContextMenuItem onClick={handleMarkAsRead}>
                <MailOpen />
                Mark as read
              </ContextMenuItem>
            )}
            <ContextMenuItem onClick={handleRemoveFromInbox}>
              <Trash2 />
              Remove from inbox
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>
      </div>
    </CSSTransition>
  );
}
